import { useState, useEffect, useCallback } from "react";
import Button from "@mui/material/Button";
import { useNavigate, useLocation, useSearchParams, useOutletContext, useParams } from "react-router-dom";
import styles from "styles/neurofeedback-session.module.css";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingButton from "@mui/lab/LoadingButton";
import DoneIcon from "@mui/icons-material/Done";
import SaveIcon from "@mui/icons-material/Save";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { RadioGroup, Radio } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PatientService from "services/PatientService";
import EnrollmentService from "services/EnrollmentService.js";
import OrganizationService from "services/OrganizationService.js";
import useAPI from "hooks/useAPI.js";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import BodyChart from "components/utils/BodyChart";
import LensMapImagesSection, {
    parseLensNfbMapImages,
} from "components/utils/LensMapImagesSection";
import { useSlashCommands } from "hooks/useSlashCommands";
import SlashCommandDropdown from "components/inputs/SlashCommandDropdown";

const DEFAULT_CURRENT_PLAN_TEMPLATE = `[x] % sec


US: 
RO: 
CBU:
S:`;

const DEFAULT_OBSERVATIONS_TEMPLATE =
    "At the end of the LENS Therapy session, the pt reports feeling [XX]";

const DEFAULT_PATIENT_REPORT_TEMPLATE =
    "The patient (Pt) arrived for their # LENS therapy appointment in 2026. It has been # week(s) since the Pt's last appt.";

const DEFAULT_PLAN_NEXT_APPOINTMENT_TEMPLATE =
    "I will likely focus on the brain sites below for the next appointment (xx)";

function SessionLensNfb() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const isCompleted = searchParams.get("isCompleted");

    const [hospitalId] = useOutletContext();
    const { patientId, enrollmentId, sessionId } = useParams();
    const location = useLocation();
    const sessionNumber = location.state?.sessionNumber;
    const createdByUserFullName = location.state?.createdByUserFullName;


    const [sessionData, setSessionData] = useState({
        sessionNumber: "",
        sessionLocationHospitalId: "",
        sessionDate: "",
        melilloPatientStatus: "",
        melilloPatientDetails: "",
        patientreportorupdates: DEFAULT_PATIENT_REPORT_TEMPLATE,
        substanceUseUpdates: "",
        substanceUseNoUpdatesThisAppointment: false,
        substanceUseNoCurrentMedications: false,
        currentPlan: DEFAULT_CURRENT_PLAN_TEMPLATE,
        completedBy: "",
        presentDuringSession: [],
        presentDuringSessionDetails: "",
        melilloHemispherePrograms: [],
        laserHemisphereProtocols: [],
        location: "",
        activeSites: [],
        tensUnitLocations: [],
        eyeLightsColor: [],
        brodmannAreas: [],
        broadmannAreasNotes: "",
        bodyChartData: "",
        bodyChartImage: "",
        lensNfbMapImages: [],
        noBodyLensCompleted: false,
        bodyLensNarrative: "",
        bodyLensNarrativeAuto: true,
        tensUnitNA: false,
        eyeLightsNA: false,
        laserTherapyNA: false,
        observations: DEFAULT_OBSERVATIONS_TEMPLATE,
        nextBrainSitePlan: [],
        planForNextAppointment: DEFAULT_PLAN_NEXT_APPOINTMENT_TEMPLATE,
        nextSessionScheduled: "",
    });

    const [patientInfo, setPatientInfo] = useState({
        name: "",
        dob: "",
    });

    const [hospitalList, setHospitalList] = useState([]);

    const getPatientInfoAPI = useAPI(
        PatientService.getPatient,
        getPatientInfoHandler
    );
    const getSessionDataAPI = useAPI(
        EnrollmentService.getSessionLens,
        getSessionDataHandler
    );
    const saveSessionAPI = useAPI(EnrollmentService.saveSessionLens, () => { });
    const submitSessionAPI = useAPI(
        EnrollmentService.submitEnrollmentSession,
        submitSessionHandler,
        true
    );
    const getOrganizationHospitalsAPI = useAPI(
        OrganizationService.getOrganizationHospitals,
        getOrganizationHospitalsHandler,
        false,
        true
    );

    function getPatientInfo() {
        getPatientInfoAPI.execute(hospitalId, patientId);
    }

    function getSessionData() {
        getSessionDataAPI.execute(hospitalId, patientId, enrollmentId, sessionId);
    }

    function saveSession() {
        saveSessionAPI.execute(hospitalId, patientId, enrollmentId, sessionId, {
            formData: buildFormPayload(sessionData),
        });
    }

    function saveSessionWithBodyChartData(bodyChartPayload) {
        try {
            const parsed = JSON.parse(bodyChartPayload);
            const hasBase64Image = Object.prototype.hasOwnProperty.call(parsed, "base64Image");
            const updatedSessionData = {
                ...sessionData,
                bodyChartData: bodyChartPayload,
                bodyChartImage: hasBase64Image ? parsed.base64Image : sessionData.bodyChartImage
            };
            setSessionData(updatedSessionData);
            saveSessionAPI.execute(hospitalId, patientId, enrollmentId, sessionId, {
                formData: buildFormPayload(updatedSessionData),
            });
        } catch (e) {
            const fallbackSessionData = {
                ...sessionData,
                bodyChartData: bodyChartPayload
            };
            setSessionData(fallbackSessionData);
            saveSessionAPI.execute(hospitalId, patientId, enrollmentId, sessionId, {
                formData: buildFormPayload(fallbackSessionData),
            });
        }
    }

    function submitSession() {
        saveSession();
        submitSessionAPI.execute(hospitalId, patientId, enrollmentId, sessionId, buildFormPayload(sessionData));
    }

    useEffect(() => {
        getPatientInfo();
        getSessionData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* API Success Handlers */
    function getPatientInfoHandler(response) {
        setPatientInfo({
            name: response.data.firstName + " " + response.data.lastName,
            dob: response.data.dob,
        });
    }

    function getOrganizationHospitalsHandler(response) {
        const list = Array.isArray(response.data) ? response.data : [];
        setHospitalList(list);
        setSessionData((prev) => {
            const rawId = prev.sessionLocationHospitalId ?? hospitalId ?? "";
            const idStr =
                rawId === undefined || rawId === null || rawId === "" ? "" : String(rawId);
            const match = idStr ? list.find((h) => String(h.id) === idStr) : null;
            return {
                ...prev,
                sessionLocationHospitalId: idStr,
                location: match?.hospitalName ?? prev.location ?? "",
            };
        });
    }

    function getTodayISODate() {
        // MUI date input expects YYYY-MM-DD
        return new Date().toISOString().split("T")[0];
    }

    function normalizeISODateOrEmpty(value) {
        if (typeof value !== "string") return "";
        return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
    }

    function migrateMelilloHemispherePrograms(formData) {
        const a = formData.melilloHemispherePrograms;
        if (Array.isArray(a) && a.length) return a;
        const h = formData.hemisphereProgram;
        if (h === "Right" || h === "Right Hemisphere Program") return ["Right Hemisphere Program"];
        if (h === "Left" || h === "Left Hemisphere Program") return ["Left Hemisphere Program"];
        return [];
    }

    function migrateLaserHemisphereProtocols(formData) {
        const a = formData.laserHemisphereProtocols;
        if (Array.isArray(a) && a.length) return a;
        const h = formData.laserHemisphereProtocol;
        if (!h || typeof h !== "string") return [];
        const out = [];
        if (h.includes("Left")) out.push("Left Hemisphere Protocol");
        if (h.includes("Right")) out.push("Right Hemisphere Protocol");
        return out;
    }

    function countBodyChartMarks(bodyChartData) {
        if (!bodyChartData) return 0;
        try {
            const parsed = JSON.parse(bodyChartData);
            const history = Array.isArray(parsed.history) ? parsed.history : [];
            return history.filter((a) => a && (a.type === "dot" || a.type === "square")).length;
        } catch {
            return 0;
        }
    }

    function buildBodyLensNarrativeFromCount(count) {
        const safeCount = Number.isFinite(count) ? count : 0;
        return `Body LENS (BL) was completed on Pt's marked areas (black dots) ${safeCount}.\nBL completed post LENS.\n# mins`;
    }

    function getSessionDataHandler(response) {
        const data = response.data;
        const rawFormData = data.formData ?? data.formdata ?? null;
        let formData = {};
        try {
            formData = rawFormData ? JSON.parse(rawFormData) : {};
        } catch (e) {
            formData = {};
        }

        if (formData && Object.keys(formData).length > 0) {
            const normalizedSessionDate = normalizeISODateOrEmpty(formData.sessionDate);
            setSessionData(prev => ({
                ...prev,
                ...formData,
                sessionNumber: formData.sessionNumber ?? prev.sessionNumber ?? "",
                sessionLocationHospitalId: (() => {
                    const v =
                        formData.sessionLocationHospitalId ??
                        prev.sessionLocationHospitalId ??
                        hospitalId;
                    return v === undefined || v === null || v === "" ? "" : String(v);
                })(),
                sessionDate: normalizedSessionDate || getTodayISODate(),
                melilloPatientStatus: formData.melilloPatientStatus ?? prev.melilloPatientStatus ?? "",
                melilloPatientDetails: formData.melilloPatientDetails ?? prev.melilloPatientDetails ?? "",
                substanceUseUpdates:
                    formData.substanceUseUpdates ?? prev.substanceUseUpdates ?? "",
                patientreportorupdates:
                    formData.patientreportorupdates ??
                    prev.patientreportorupdates ??
                    DEFAULT_PATIENT_REPORT_TEMPLATE,
                currentPlan: Object.prototype.hasOwnProperty.call(formData, "currentPlan")
                    ? formData.currentPlan
                    : (prev.currentPlan ?? DEFAULT_CURRENT_PLAN_TEMPLATE),
                observations: Object.prototype.hasOwnProperty.call(formData, "observations")
                    ? formData.observations
                    : (prev.observations ?? DEFAULT_OBSERVATIONS_TEMPLATE),
                noBodyLensCompleted: !!formData.noBodyLensCompleted,
                lensNfbMapImages: parseLensNfbMapImages(formData.lensNfbMapImages),
                bodyLensNarrative: formData.bodyLensNarrative ?? prev.bodyLensNarrative ?? "",
                bodyLensNarrativeAuto: formData.bodyLensNarrativeAuto ?? prev.bodyLensNarrativeAuto ?? true,
                tensUnitNA: !!formData.tensUnitNA,
                eyeLightsNA: !!formData.eyeLightsNA,
                laserTherapyNA: !!formData.laserTherapyNA,
                substanceUseNoUpdatesThisAppointment: !!formData.substanceUseNoUpdatesThisAppointment,
                substanceUseNoCurrentMedications: !!formData.substanceUseNoCurrentMedications,
                melilloHemispherePrograms: migrateMelilloHemispherePrograms(formData),
                laserHemisphereProtocols: migrateLaserHemisphereProtocols(formData),
                planForNextAppointment:
                    (formData.planForNextAppointment != null &&
                        String(formData.planForNextAppointment).trim() !== "")
                        ? formData.planForNextAppointment
                        : (prev.planForNextAppointment || DEFAULT_PLAN_NEXT_APPOINTMENT_TEMPLATE),
                nextSessionScheduled: normalizeNextSessionScheduled(
                    formData.nextSessionScheduled ?? prev.nextSessionScheduled ?? ""
                ),
            }));
        } else {
            const defaultDate = getTodayISODate();
            setSessionData(prev => ({
                ...prev,
                completedBy: createdByUserFullName,
                sessionNumber: sessionNumber ?? prev.sessionNumber ?? "",
                sessionLocationHospitalId: (() => {
                    const v = prev.sessionLocationHospitalId ?? hospitalId ?? "";
                    return v === undefined || v === null || v === "" ? "" : String(v);
                })(),
                sessionDate: defaultDate,
                substanceUseUpdates: prev.substanceUseUpdates ?? "",
                patientreportorupdates: prev.patientreportorupdates || DEFAULT_PATIENT_REPORT_TEMPLATE,
                currentPlan: prev.currentPlan || DEFAULT_CURRENT_PLAN_TEMPLATE,
                observations: prev.observations || DEFAULT_OBSERVATIONS_TEMPLATE,
                planForNextAppointment:
                    prev.planForNextAppointment || DEFAULT_PLAN_NEXT_APPOINTMENT_TEMPLATE,
                melilloHemispherePrograms: [],
                laserHemisphereProtocols: [],
            }));
        }
    }

    function normalizeNextSessionScheduled(raw) {
        if (!raw || typeof raw !== "string") return "";
        const legacy = {
            "within Week": "Within Week",
            "1 Week": "1 week",
            "2 Weeks": "2 week",
            "3 Weeks": "3 weeks",
            "1 Month": "1 month",
        };
        return legacy[raw] || raw;
    }

    function buildFormPayload(data) {
        const laserJoined = (data.laserHemisphereProtocols || []).filter(Boolean).join(", ");
        return {
            ...data,
            hemisphereProgram: data.melilloHemispherePrograms?.[0] ?? data.hemisphereProgram ?? "",
            laserHemisphereProtocol: laserJoined || data.laserHemisphereProtocol || "",
        };
    }

    function submitSessionHandler() {
        navigate(-1);
    }

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === "checkbox") {
            if (name.includes("[]")) {
                // Multi-select checkboxes (arrays)
                const fieldName = name.replace("[]", "");
                setSessionData(prev => {
                    const currentArray = prev[fieldName] || [];
                    const newArray = checked
                        ? [...currentArray, value]
                        : currentArray.filter(item => item !== value);
                    return { ...prev, [fieldName]: newArray };
                });
            } else {
                // Single-select checkboxes (radio button behavior)
                setSessionData(prev => {
                    const currentValue = prev[name];
                    // If the same value is clicked, toggle it off, otherwise set the new value
                    const newValue = currentValue === value ? "" : value;
                    return { ...prev, [name]: newValue };
                });
            }
        } else {
            setSessionData(prev => ({ ...prev, [name]: value }));
        }
    };

    const setFieldValue = useCallback((name, value) => {
        setSessionData(prev => ({ ...prev, [name]: value }));
    }, []);

    const { getSlashProps, dropdownState, applyShortcut } = useSlashCommands(setFieldValue);

    const handlePresentDuringSessionChange = (e) => {
        const { value, checked } = e.target;

        setSessionData(prev => {
            const currentSelections = prev.presentDuringSession || [];
            let newSelections = currentSelections;

            if (value === "N/A") {
                newSelections = checked ? ["N/A"] : [];
            } else if (checked) {
                newSelections = [...currentSelections.filter(item => item !== "N/A"), value];
            } else {
                newSelections = currentSelections.filter(item => item !== value);
            }

            return {
                ...prev,
                presentDuringSession: newSelections,
            };
        });
    };

    const brodmannAreas = [
        "BA 1", "BA 2", "BA 3", "BA 4", "BA 5", "BA 6", "BA 7", "BA 8", "BA 9", "BA 10",
        "BA 11", "BA 12", "BA 13", "BA 14", "BA 15", "BA 16", "BA 17", "BA 18", "BA 19", "BA 20",
        "BA 21", "BA 22", "BA 23", "BA 24", "BA 25", "BA 26", "BA 27", "BA 28", "BA 29", "BA 30",
        "BA 31", "BA 32", "BA 33", "BA 34", "BA 35", "BA 36", "BA 37", "BA 38", "BA 39", "BA 40",
        "BA 41", "BA 42", "BA 43", "BA 44", "BA 45", "BA 46", "BA 47"
    ];

    const activeSites = [
        "Fp1", "Fp2", "F3", "F4", "Fpz", "Fz", "F7", "F8", "C3", "C4", "Cz",
        "T3", "T4", "T5", "T6", "P3", "P4", "Pz", "O1", "O2", "Oz"
    ];

    const nextSessionScheduledOptions = [
        "Within Week",
        "1 week",
        "2 week",
        "3 weeks",
        "1 month",
    ];

    const handleSessionLocationHospitalChange = (e) => {
        const id = e.target.value;
        const selected = hospitalList.find((h) => String(h.id) === String(id));
        setSessionData((prev) => ({
            ...prev,
            sessionLocationHospitalId: id,
            location: selected?.hospitalName ?? "",
        }));
    };

    const handleMelilloPatientStatusChange = (e) => {
        const value = e.target.value;
        setSessionData((prev) => ({
            ...prev,
            melilloPatientStatus: value,
            ...(value === "No"
                ? { melilloPatientDetails: "", melilloHemispherePrograms: [] }
                : {}),
        }));
    };

    // State to control "Saved" message visibility
    const [showSaved, setShowSaved] = useState(false);

    // Effect to auto-hide the "Saved" message after 2 seconds
    useEffect(() => {
        if (saveSessionAPI.status === "success") {
            setShowSaved(true);

            const timer = setTimeout(() => setShowSaved(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [saveSessionAPI.status]);
    useEffect(() => {
        // Only set if completedBy is empty and we have createdByUserFullName
        if (createdByUserFullName && !sessionData.completedBy) {
            setSessionData(prev => ({
                ...prev,
                completedBy: createdByUserFullName
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdByUserFullName]);

    return (
        <Paper elevation={0} className="p-4">
            <div className="d-flex justify-content-between align-items-start">
                <div className="d-flex align-items-center gap-3">
                    <Typography variant="h2" gutterBottom>
                        LENS Neurofeedback Session
                    </Typography>
                    <TextField
                        name="sessionNumber"
                        value={sessionData.sessionNumber}
                        onChange={(e) =>
                            setSessionData((prev) => ({
                                ...prev,
                                sessionNumber: e.target.value,
                            }))
                        }
                        size="small"
                        sx={{ width: 120 }}
                        placeholder="Session #"
                        inputProps={{ "aria-label": "Session number" }}
                        InputProps={{
                            readOnly: !!getSessionDataAPI.data?.data?.formData && !!sessionData.sessionNumber,
                        }}
                    />
                </div>
                <Button onClick={() => navigate(-1)} variant="outlined">
                    Back
                </Button>
            </div>

            <form>
                {getPatientInfoAPI.status === "pending" ||
                    getSessionDataAPI.status === "pending" ||
                    getOrganizationHospitalsAPI.status === "pending" ? (
                    <div className="text-center">
                        <CircularProgress className="text-center" sx={{ my: 20 }} />
                    </div>
                ) : (
                    <div className="mt-4">
                        {/* Patient Info Section */}
                        <Paper elevation={1} className="p-4 mb-4">
                            <Typography variant="h3" mb={2}>Patient Information</Typography>
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <TextField
                                        label="Patient Name"
                                        value={patientInfo.name}
                                        fullWidth
                                        InputProps={{ readOnly: true }}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <TextField
                                        label="Date of Birth"
                                        value={patientInfo.dob}
                                        fullWidth
                                        InputProps={{ readOnly: true }}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <TextField
                                        label="Date of Session"
                                        name="sessionDate"
                                        type="date"
                                        value={sessionData.sessionDate}
                                        onChange={(e) =>
                                            setSessionData((prev) => ({
                                                ...prev,
                                                sessionDate: e.target.value,
                                            }))
                                        }
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </div>
                            </div>

                            <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                                Location of session
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Select a hospital in your organization.
                            </Typography>
                            {hospitalList.length === 0 && getOrganizationHospitalsAPI.status === "success" ? (
                                <Typography variant="body2" color="text.secondary">
                                    No hospitals are available for your organization.
                                </Typography>
                            ) : (
                                <FormControl component="fieldset" variant="standard" sx={{ width: "100%" }}>
                                    <RadioGroup
                                        row
                                        name="sessionLocationHospitalId"
                                        value={
                                            sessionData.sessionLocationHospitalId
                                                ? String(sessionData.sessionLocationHospitalId)
                                                : hospitalId
                                                    ? String(hospitalId)
                                                    : ""
                                        }
                                        onChange={handleSessionLocationHospitalChange}
                                        sx={{ flexWrap: "wrap", gap: 1 }}
                                    >
                                        {hospitalList.map((hospital) => (
                                            <FormControlLabel
                                                key={hospital.id}
                                                value={String(hospital.id)}
                                                control={<Radio />}
                                                label={hospital.hospitalName}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            )}

                            <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
                                Past/Current Melillo Method Patient
                            </Typography>
                            <FormControl component="fieldset" variant="standard">
                                <RadioGroup
                                    row
                                    name="melilloPatientStatus"
                                    value={sessionData.melilloPatientStatus}
                                    onChange={handleMelilloPatientStatusChange}
                                >
                                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                    <FormControlLabel value="No" control={<Radio />} label="No" />
                                </RadioGroup>
                            </FormControl>

                            {sessionData.melilloPatientStatus === "Yes" && (
                                <>
                                    <TextField
                                        name="melilloPatientDetails"
                                        value={sessionData.melilloPatientDetails || ""}
                                        {...getSlashProps("melilloPatientDetails", handleInputChange)}
                                        fullWidth
                                        multiline
                                        rows={2}
                                        sx={{ mt: 2 }}
                                    />
                                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                                        Hemisphere program
                                    </Typography>
                                    <Stack direction="row" spacing={2} flexWrap="wrap" className={styles["checkbox-group"]}>
                                        {["Right Hemisphere Program", "Left Hemisphere Program"].map((label) => (
                                            <FormControlLabel
                                                key={label}
                                                control={
                                                    <Checkbox
                                                        name="melilloHemispherePrograms[]"
                                                        value={label}
                                                        checked={sessionData.melilloHemispherePrograms?.includes(label)}
                                                        onChange={handleInputChange}
                                                    />
                                                }
                                                label={label}
                                            />
                                        ))}
                                    </Stack>
                                </>
                            )}

                            <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>
                                Present During Session
                            </Typography>
                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                {["Both Parents", "Mother", "Father", "Spouse", "Friend", "N/A"].map(option => (
                                    <FormControlLabel
                                        key={`present-${option}`}
                                        control={
                                            <Checkbox
                                                name="presentDuringSession[]"
                                                value={option}
                                                checked={sessionData.presentDuringSession?.includes(option)}
                                                onChange={handlePresentDuringSessionChange}
                                            />
                                        }
                                        label={option}
                                    />
                                ))}
                            </Stack>

                            {sessionData.presentDuringSession?.some(option => option !== "N/A") && (
                                <TextField
                                    name="presentDuringSessionDetails"
                                    value={sessionData.presentDuringSessionDetails || ""}
                                    {...getSlashProps('presentDuringSessionDetails', handleInputChange)}
                                    fullWidth
                                    multiline
                                    rows={2}
                                    sx={{ mt: 2 }}
                                />
                            )}
                        </Paper>

                        <Paper elevation={1} className="p-4 mb-4">
                            <Typography variant="subtitle1" gutterBottom>
                                Patient Report/Update
                            </Typography>
                            <TextField
                                name="patientreportorupdates"
                                value={sessionData.patientreportorupdates ?? ""}
                                {...getSlashProps('patientreportorupdates', handleInputChange)}
                                fullWidth
                                multiline
                                rows={4}
                                sx={{ mt: 1 }}
                            />

                            <Typography variant="subtitle1" gutterBottom className="mt-3">
                                Substance Use Updates (ex: alcohol, drugs, prescription medication)
                            </Typography>
                            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!sessionData.substanceUseNoUpdatesThisAppointment}
                                            onChange={(e) =>
                                                setSessionData((prev) => ({
                                                    ...prev,
                                                    substanceUseNoUpdatesThisAppointment: e.target.checked,
                                                }))
                                            }
                                        />
                                    }
                                    label="No updates this appointment"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!sessionData.substanceUseNoCurrentMedications}
                                            onChange={(e) =>
                                                setSessionData((prev) => ({
                                                    ...prev,
                                                    substanceUseNoCurrentMedications: e.target.checked,
                                                }))
                                            }
                                        />
                                    }
                                    label="No current medications"
                                />
                            </Stack>
                            <TextField
                                name="substanceUseUpdates"
                                value={sessionData.substanceUseUpdates ?? ""}
                                {...getSlashProps("substanceUseUpdates", handleInputChange)}
                                fullWidth
                                multiline
                                rows={3}
                                sx={{ mt: 1 }}
                            />

                            <Typography variant="subtitle1" gutterBottom className="mt-3">
                                Current Plan
                            </Typography>
                            <TextField
                                name="currentPlan"
                                value={sessionData.currentPlan ?? ""}
                                {...getSlashProps('currentPlan', handleInputChange)}
                                fullWidth
                                multiline
                                minRows={10}
                                sx={{ mt: 1 }}
                            />

                            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                                Brain Sites Completed
                            </Typography>
                            <FormControlLabel
                                sx={{ mt: 1 }}
                                control={
                                    <Checkbox
                                        checked={
                                            Array.isArray(sessionData.activeSites) &&
                                            sessionData.activeSites.length === activeSites.length
                                        }
                                        indeterminate={
                                            Array.isArray(sessionData.activeSites) &&
                                            sessionData.activeSites.length > 0 &&
                                            sessionData.activeSites.length < activeSites.length
                                        }
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setSessionData((prev) => ({
                                                ...prev,
                                                activeSites: checked ? [...activeSites] : [],
                                            }));
                                        }}
                                    />
                                }
                                label="Select All"
                            />
                            <div className={`${styles["checkbox-group"]} mt-2`}>
                                {activeSites.map(area => (
                                    <FormControlLabel
                                        key={area}
                                        control={
                                            <Checkbox
                                                name="activeSites[]"
                                                value={area}
                                                checked={sessionData.activeSites?.includes(area)}
                                                onChange={handleInputChange}
                                            />
                                        }
                                        label={area}
                                    />
                                ))}
                            </div>
                        </Paper>

                        <LensMapImagesSection
                            value={sessionData.lensNfbMapImages}
                            onChange={(nextImages) =>
                                setSessionData((prev) => ({
                                    ...prev,
                                    lensNfbMapImages: nextImages,
                                }))
                            }
                            disabled={isCompleted !== "false"}
                        />

                        <Accordion defaultExpanded sx={{ mb: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight={600}>Body LENS (chart)</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <FormControlLabel
                                    sx={{ mb: 2, display: "block" }}
                                    control={
                                        <Checkbox
                                            name="noBodyLensCompleted"
                                            checked={!!sessionData.noBodyLensCompleted}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setSessionData((prev) => ({
                                                    ...prev,
                                                    noBodyLensCompleted: checked,
                                                }));
                                            }}
                                        />
                                    }
                                    label="No BL (Body LENS) Completed"
                                />
                                {!sessionData.noBodyLensCompleted && (
                                    <>
                                        <BodyChart
                                            value={sessionData.bodyChartData}
                                            imageUrl={sessionData.bodyChartImage}
                                            onSaveImage={saveSessionWithBodyChartData}
                                            onChange={(data) => {
                                                let nextImageBase64;
                                                try {
                                                    const parsed = JSON.parse(data);
                                                    const hasBase64Image = Object.prototype.hasOwnProperty.call(parsed, "base64Image");
                                                    nextImageBase64 = hasBase64Image ? parsed.base64Image : undefined;
                                                } catch (err) {
                                                    nextImageBase64 = undefined;
                                                }

                                                setSessionData((prev) => {
                                                    const markCount = countBodyChartMarks(data);
                                                    const shouldAuto =
                                                        prev.bodyLensNarrativeAuto !== false &&
                                                        !prev.noBodyLensCompleted &&
                                                        markCount > 0;

                                                    return {
                                                        ...prev,
                                                        bodyChartData: data,
                                                        bodyChartImage: nextImageBase64 ?? prev.bodyChartImage,
                                                        bodyLensNarrative:
                                                            shouldAuto ? buildBodyLensNarrativeFromCount(markCount) : prev.bodyLensNarrative,
                                                    };
                                                });
                                            }}
                                        />
                                        <TextField
                                            name="bodyLensNarrative"
                                            value={
                                                sessionData.bodyLensNarrative ||
                                                (countBodyChartMarks(sessionData.bodyChartData) > 0
                                                    ? buildBodyLensNarrativeFromCount(countBodyChartMarks(sessionData.bodyChartData))
                                                    : "")
                                            }
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setSessionData((prev) => ({
                                                    ...prev,
                                                    bodyLensNarrative: val,
                                                    bodyLensNarrativeAuto: false,
                                                }));
                                            }}
                                            fullWidth
                                            multiline
                                            rows={3}
                                            sx={{ mt: 2 }}
                                        />
                                    </>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={
                                !(sessionData.tensUnitNA && sessionData.eyeLightsNA && sessionData.laserTherapyNA)
                            }
                            sx={{ mb: 2 }}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography fontWeight={600}>Additional Devices Used</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="subtitle2" gutterBottom>TENS Unit</Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="tensUnitNA"
                                            checked={!!sessionData.tensUnitNA}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setSessionData((prev) => ({
                                                    ...prev,
                                                    tensUnitNA: checked,
                                                    tensUnitLocations: checked ? [] : prev.tensUnitLocations,
                                                }));
                                            }}
                                        />
                                    }
                                    label="N/A"
                                />
                                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                    {!sessionData.tensUnitNA && ["Left Upper Back", "Right Upper Back", "Left Lower Back", "Right Lower Back"].map(location => (
                                        <FormControlLabel
                                            key={location}
                                            control={
                                                <Checkbox
                                                    name="tensUnitLocations[]"
                                                    value={location}
                                                    checked={sessionData.tensUnitLocations?.includes(location)}
                                                    onChange={handleInputChange}
                                                />
                                            }
                                            label={location}
                                        />
                                    ))}
                                </Stack>

                                <Typography variant="subtitle2" className="mt-3">Eye Lights</Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="eyeLightsNA"
                                            checked={!!sessionData.eyeLightsNA}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setSessionData((prev) => ({
                                                    ...prev,
                                                    eyeLightsNA: checked,
                                                    eyeLightsColor: checked ? [] : prev.eyeLightsColor,
                                                }));
                                            }}
                                        />
                                    }
                                    label="N/A"
                                />
                                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                    {!sessionData.eyeLightsNA && ["Red", "Blue"].map(color => (
                                        <FormControlLabel
                                            key={color}
                                            control={
                                                <Checkbox
                                                    name="eyeLightsColor[]"
                                                    value={color}
                                                    checked={sessionData.eyeLightsColor?.includes(color)}
                                                    onChange={handleInputChange}
                                                />
                                            }
                                            label={color}
                                        />
                                    ))}
                                </Stack>

                                <Typography variant="subtitle2" className="mt-3">Laser Therapy</Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="laserTherapyNA"
                                            checked={!!sessionData.laserTherapyNA}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                setSessionData((prev) => ({
                                                    ...prev,
                                                    laserTherapyNA: checked,
                                                    laserHemisphereProtocols: checked ? [] : prev.laserHemisphereProtocols,
                                                    brodmannAreas: checked ? [] : prev.brodmannAreas,
                                                    broadmannAreasNotes: checked ? "" : prev.broadmannAreasNotes,
                                                }));
                                            }}
                                        />
                                    }
                                    label="N/A"
                                />

                                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Hemispheric Protocol Focus</Typography>
                                <div className={styles["checkbox-group"]}>
                                    {!sessionData.laserTherapyNA && ["Left Hemisphere Protocol", "Right Hemisphere Protocol"].map((label) => (
                                        <FormControlLabel
                                            key={label}
                                            control={
                                                <Checkbox
                                                    name="laserHemisphereProtocols[]"
                                                    value={label}
                                                    checked={sessionData.laserHemisphereProtocols?.includes(label)}
                                                    onChange={handleInputChange}
                                                />
                                            }
                                            label={label}
                                        />
                                    ))}
                                </div>

                                <Typography variant="subtitle2" sx={{ mt: 3 }}>Targeted Brodmann Areas</Typography>
                                <div className={`${styles["checkbox-group"]} mt-1`}>
                                    {!sessionData.laserTherapyNA && brodmannAreas.map(area => (
                                        <FormControlLabel
                                            key={area}
                                            control={
                                                <Checkbox
                                                    name="brodmannAreas[]"
                                                    value={area}
                                                    checked={sessionData.brodmannAreas?.includes(area)}
                                                    onChange={handleInputChange}
                                                />
                                            }
                                            label={area}
                                        />
                                    ))}
                                </div>

                                <TextField
                                    name="broadmannAreasNotes"
                                    value={sessionData.broadmannAreasNotes ?? ""}
                                    {...getSlashProps('broadmannAreasNotes', handleInputChange)}
                                    fullWidth
                                    multiline
                                    rows={2}
                                    className="mt-2"
                                />
                            </AccordionDetails>
                        </Accordion>

                        <Paper elevation={1} className="p-4 mb-4">
                            <Typography variant="subtitle1" gutterBottom>
                                Observations
                            </Typography>
                            <TextField
                                name="observations"
                                value={sessionData.observations ?? ""}
                                {...getSlashProps("observations", handleInputChange)}
                                fullWidth
                                multiline
                                rows={3}
                                sx={{ mt: 1 }}
                            />

                            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                                Plan for next appointment
                            </Typography>
                            <TextField
                                name="planForNextAppointment"
                                value={sessionData.planForNextAppointment ?? ""}
                                {...getSlashProps('planForNextAppointment', handleInputChange)}
                                fullWidth
                                multiline
                                rows={3}
                                sx={{ mt: 1 }}
                            />

                            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                                Next Appointment Brain Site Plan
                            </Typography>
                            <FormControlLabel
                                sx={{ mt: 1 }}
                                control={
                                    <Checkbox
                                        checked={
                                            Array.isArray(sessionData.nextBrainSitePlan) &&
                                            sessionData.nextBrainSitePlan.length === activeSites.length
                                        }
                                        indeterminate={
                                            Array.isArray(sessionData.nextBrainSitePlan) &&
                                            sessionData.nextBrainSitePlan.length > 0 &&
                                            sessionData.nextBrainSitePlan.length < activeSites.length
                                        }
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setSessionData((prev) => ({
                                                ...prev,
                                                nextBrainSitePlan: checked ? [...activeSites] : [],
                                            }));
                                        }}
                                    />
                                }
                                label="Select All"
                            />
                            <div className={`${styles["checkbox-group"]} mt-1`}>
                                {activeSites.map(area => (
                                    <FormControlLabel
                                        key={`next-${area}`}
                                        control={
                                            <Checkbox
                                                name="nextBrainSitePlan[]"
                                                value={area}
                                                checked={sessionData.nextBrainSitePlan?.includes(area)}
                                                onChange={handleInputChange}
                                            />
                                        }
                                        label={area}
                                    />
                                ))}
                            </div>

                            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                                Next Follow Up Scheduled
                            </Typography>
                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                <FormControl>
                                    <RadioGroup
                                        row
                                        name="nextSessionScheduled"
                                        value={sessionData.nextSessionScheduled}
                                        onChange={handleInputChange}
                                    >
                                        {nextSessionScheduledOptions.map(option => (
                                            <FormControlLabel
                                                key={option}
                                                value={option}
                                                control={<Radio />}
                                                label={option}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            </Stack>

                            <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                                Completed By
                            </Typography>
                            <TextField
                                name="completedBy"
                                value={sessionData.completedBy || createdByUserFullName || ""}
                                {...getSlashProps('completedBy', handleInputChange)}
                                fullWidth
                                sx={{ mt: 1 }}
                            />
                        </Paper>

                        {/* Submit Button */}
                        {isCompleted === "false" && (
                            <div className="d-flex align-items-center mb-3">
                                <LoadingButton
                                    loading={saveSessionAPI.status === "pending"}
                                    loadingPosition="start"
                                    startIcon={<SaveIcon />}
                                    variant="outlined"
                                    className="me-3"
                                    onClick={saveSession}
                                    type="button"
                                >
                                    Save
                                </LoadingButton>
                                {submitSessionAPI.status === "pending" ? (
                                    <LoadingButton
                                        loading
                                        loadingPosition="start"
                                        startIcon={<SaveIcon />}
                                        variant="contained"
                                        className="me-3"
                                    >
                                        Submit
                                    </LoadingButton>
                                ) : (
                                    <Button
                                        type="submit"
                                        className="me-3"
                                        startIcon={<DoneIcon />}
                                        variant="contained"
                                        onClick={submitSession}
                                    >
                                        Submit
                                    </Button>
                                )}
                                {showSaved && (
                                    <Typography color="success.main">Saved</Typography>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </form>
            <SlashCommandDropdown state={dropdownState} onSelect={applyShortcut} />
        </Paper >
    );
}

export default SessionLensNfb;
